/**
 * SeedDrive V2 — Role and Permission Configuration
 *
 * Single source of truth for:
 * - Role identifiers (ROLES)
 * - Role display metadata (ROLE_PROFILES)
 * - Navigation section permissions per role (ROLE_NAV_PERMISSIONS)
 * - Module-level action permissions per role (ROLE_MODULE_PERMISSIONS)
 *
 * Rules:
 * - Nav items in navConfig.js reference role keys from ROLES — not magic strings.
 * - All permission checks import from this file.
 * - No role logic scattered in components — check permissions here centrally.
 * - Prototype implementation: mock enforcement only. No backend RBAC.
 *
 * Phase classification:
 *   mvp      — represented in the prototype
 *   later    — placeholder / feature-flagged only
 */

// ─── Role Identifiers ─────────────────────────────────────────────────────────

export const ROLES = {
  // Internal MVP roles
  INVESTMENT_TEAM:   'investment_team',
  IC_MEMBER:         'ic_member',
  ASSET_MGMT_TEAM:   'asset_mgmt_team',
  FINANCE_TEAM:      'finance_team',
  LEGAL_COMP_TEAM:   'legal_comp_team',
  PLATFORM_ADMIN:    'platform_admin',

  // External deal-scoped roles (MVP)
  EXTERNAL_INVESTOR: 'external_investor',
  DEAL_ORIGINATOR:   'deal_originator',
  CO_INVESTOR:       'co_investor',

  // Later phase (not active in prototype navigation)
  BD_TEAM:           'bd_team',        // later — INVESTMENT_TEAM inherits sourcing for MVP
  LP_PORTAL_USER:    'lp_portal_user', // later
  OBSERVER:          'observer',        // later
};

// ─── Role Profiles ────────────────────────────────────────────────────────────
// Display metadata for the role switcher and user context banner.

export const ROLE_PROFILES = {
  [ROLES.INVESTMENT_TEAM]: {
    label: 'Investment Team',
    description: 'Deal sourcing, structuring, and IC management',
    category: 'internal',
    phase: 'mvp',
    color: '#2563eb',
    initials: 'IT',
  },
  [ROLES.IC_MEMBER]: {
    label: 'IC Member',
    description: 'Investment Committee review and approval',
    category: 'internal',
    phase: 'mvp',
    color: '#6366f1',
    initials: 'IC',
  },
  [ROLES.ASSET_MGMT_TEAM]: {
    label: 'Asset Management',
    description: 'Operational oversight and performance monitoring',
    category: 'internal',
    phase: 'mvp',
    color: '#0891b2',
    initials: 'AM',
  },
  [ROLES.FINANCE_TEAM]: {
    label: 'Finance Team',
    description: 'Valuations, reporting, and financial controls',
    category: 'internal',
    phase: 'mvp',
    color: '#059669',
    initials: 'FT',
  },
  [ROLES.LEGAL_COMP_TEAM]: {
    label: 'Legal & Compliance',
    description: 'Governance, compliance, and legal records',
    category: 'internal',
    phase: 'mvp',
    color: '#d97706',
    initials: 'LC',
  },
  [ROLES.PLATFORM_ADMIN]: {
    label: 'Platform Admin',
    description: 'Full platform access and user management',
    category: 'internal',
    phase: 'mvp',
    color: '#dc2626',
    initials: 'PA',
  },
  [ROLES.EXTERNAL_INVESTOR]: {
    label: 'External Investor',
    description: 'Marketplace access and deal room (scoped)',
    category: 'external',
    phase: 'mvp',
    color: '#7c3aed',
    initials: 'EI',
  },
  [ROLES.DEAL_ORIGINATOR]: {
    label: 'Deal Originator',
    description: 'Deal submission and limited collaboration access',
    category: 'external',
    phase: 'mvp',
    color: '#b45309',
    initials: 'DO',
  },
  [ROLES.CO_INVESTOR]: {
    label: 'Co-Investor',
    description: 'SPV co-investment and commitment tracking',
    category: 'external',
    phase: 'mvp',
    color: '#0f766e',
    initials: 'CI',
  },
  [ROLES.BD_TEAM]: {
    label: 'BD Team',
    description: 'Business development — later phase',
    category: 'internal',
    phase: 'later',
    color: '#94a3b8',
    initials: 'BD',
  },
  [ROLES.LP_PORTAL_USER]: {
    label: 'LP Portal User',
    description: 'LP self-service portal — later phase',
    category: 'external',
    phase: 'later',
    color: '#94a3b8',
    initials: 'LP',
  },
  [ROLES.OBSERVER]: {
    label: 'Observer',
    description: 'Read-only board / regulator access — later phase',
    category: 'external',
    phase: 'later',
    color: '#94a3b8',
    initials: 'OB',
  },
};

// ─── MVP Role Switcher Options ────────────────────────────────────────────────
// Ordered list for the GlobalHeader role switcher — MVP roles only.

export const ROLE_SWITCHER_OPTIONS = [
  // Internal
  { group: 'Internal', role: ROLES.INVESTMENT_TEAM },
  { group: 'Internal', role: ROLES.IC_MEMBER },
  { group: 'Internal', role: ROLES.ASSET_MGMT_TEAM },
  { group: 'Internal', role: ROLES.FINANCE_TEAM },
  { group: 'Internal', role: ROLES.LEGAL_COMP_TEAM },
  { group: 'Internal', role: ROLES.PLATFORM_ADMIN },
  // External
  { group: 'External', role: ROLES.EXTERNAL_INVESTOR },
  { group: 'External', role: ROLES.DEAL_ORIGINATOR },
  { group: 'External', role: ROLES.CO_INVESTOR },
];

// ─── Navigation Section Permissions ──────────────────────────────────────────
// Maps each role to the nav section ids it can access.
// navConfig.js permission arrays reference these role key strings.
//
// All internal roles that are not listed for a section will NOT see it in the
// sidebar. External roles get a dramatically reduced nav shape.

export const ROLE_NAV_PERMISSIONS = {
  [ROLES.INVESTMENT_TEAM]: [
    'dashboard', 'marketplace', 'opportunities',
    'deals', 'pipeline', 'structuringHub', 'icSubmission', 'spvDashboard', 'investorOnboarding',
    'portfolio', 'portfolioAssets', 'assetLifecycle', 'valuationView', 'portfolioHeatmap', 'vintageAnalysis',
    'assetManagement', 'operationsConsole', 'oversightHub', 'reviewPacks', 'riskMonitor', 'valueCreation', 'watchlist',
    'operations', 'dataRoom', 'esgReporting', 'governanceRecords', 'counterparties', 'auditTrail',
    'activityFeed',
    'transactions', 'tokenisation', 'tokenHoldings', 'tokenTransfer', 'tokenGovernance',
    'communications', 'messages', 'notifications',
    'teamManagement', 'settings',
  ],
  [ROLES.IC_MEMBER]: [
    'dashboard',
    'deals', 'icSubmission', 'spvDashboard',
    'portfolio', 'portfolioAssets', 'valuationView',
    'activityFeed',
    'communications', 'messages', 'notifications',
    'settings',
  ],
  [ROLES.ASSET_MGMT_TEAM]: [
    'dashboard',
    'portfolio', 'portfolioAssets', 'assetLifecycle', 'valuationView', 'portfolioHeatmap', 'vintageAnalysis',
    'assetManagement', 'operationsConsole', 'oversightHub', 'reviewPacks', 'riskMonitor', 'valueCreation', 'watchlist',
    'operations', 'dataRoom', 'esgReporting', 'governanceRecords', 'auditTrail',
    'activityFeed',
    'communications', 'messages', 'notifications',
    'settings',
  ],
  [ROLES.FINANCE_TEAM]: [
    'dashboard',
    'portfolio', 'portfolioAssets', 'assetLifecycle', 'valuationView', 'portfolioHeatmap', 'vintageAnalysis',
    'assetManagement', 'reviewPacks', 'riskMonitor',
    'operations', 'dataRoom', 'esgReporting', 'governanceRecords', 'auditTrail',
    'activityFeed',
    'communications', 'messages', 'notifications',
    'settings',
  ],
  [ROLES.LEGAL_COMP_TEAM]: [
    'dashboard',
    'deals', 'investorOnboarding', 'spvDashboard',
    'operations', 'dataRoom', 'governanceRecords', 'counterparties', 'auditTrail',
    'activityFeed',
    'communications', 'messages', 'notifications',
    'settings', 'permissions',
  ],
  [ROLES.PLATFORM_ADMIN]: [
    'dashboard', 'marketplace', 'opportunities',
    'deals', 'pipeline', 'structuringHub', 'icSubmission', 'spvDashboard', 'investorOnboarding',
    'portfolio', 'portfolioAssets', 'assetLifecycle', 'valuationView', 'portfolioHeatmap', 'vintageAnalysis',
    'assetManagement', 'operationsConsole', 'oversightHub', 'reviewPacks', 'riskMonitor', 'valueCreation', 'watchlist',
    'operations', 'dataRoom', 'esgReporting', 'governanceRecords', 'counterparties', 'auditTrail',
    'activityFeed',
    'transactions', 'tokenisation', 'tokenHoldings', 'tokenTransfer', 'tokenAdmin', 'tokenGovernance',
    'communications', 'messages', 'notifications',
    'teamManagement', 'settings', 'permissions',
  ],
  // External roles — scoped nav only
  [ROLES.EXTERNAL_INVESTOR]: [
    'marketplace', 'opportunities',
    'dealRoom',
    'communications', 'messages', 'notifications',
    'settings',
  ],
  [ROLES.DEAL_ORIGINATOR]: [
    'marketplace', 'opportunities',
    'dealRoom',
    'communications', 'messages', 'notifications',
    'settings',
  ],
  [ROLES.CO_INVESTOR]: [
    'marketplace', 'opportunities',
    'deals', 'spvDashboard',
    'dealRoom',
    'communications', 'messages', 'notifications',
    'settings',
  ],
};

// ─── Module-Level Action Permissions ─────────────────────────────────────────
// Granular action permissions used by pages to show/hide actions and states.
// Prototype implementation: pages read these to gate CTAs and UI panels.
// Not enforced by backend — purely visual.

export const ROLE_MODULE_PERMISSIONS = {
  [ROLES.INVESTMENT_TEAM]: {
    canSourceDeals: true,
    canSubmitIC: true,
    canApproveIC: false,
    canStructureSPV: true,
    canOnboardInvestors: true,
    canViewICSubmissions: true,
    canViewInternalDataRoom: true,
    canViewExternalDealRoom: false,
    canTokenise: true,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: true,
    canViewOperations: true,
  },
  [ROLES.IC_MEMBER]: {
    canSourceDeals: false,
    canSubmitIC: false,
    canApproveIC: true,
    canStructureSPV: false,
    canOnboardInvestors: false,
    canViewICSubmissions: true,
    canViewInternalDataRoom: true,
    canViewExternalDealRoom: false,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: true,
    canViewOperations: false,
  },
  [ROLES.ASSET_MGMT_TEAM]: {
    canSourceDeals: false,
    canSubmitIC: false,
    canApproveIC: false,
    canStructureSPV: false,
    canOnboardInvestors: false,
    canViewICSubmissions: false,
    canViewInternalDataRoom: true,
    canViewExternalDealRoom: false,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: true,
    canViewOperations: true,
  },
  [ROLES.FINANCE_TEAM]: {
    canSourceDeals: false,
    canSubmitIC: false,
    canApproveIC: false,
    canStructureSPV: false,
    canOnboardInvestors: false,
    canViewICSubmissions: false,
    canViewInternalDataRoom: true,
    canViewExternalDealRoom: false,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: true,
    canViewOperations: true,
  },
  [ROLES.LEGAL_COMP_TEAM]: {
    canSourceDeals: false,
    canSubmitIC: false,
    canApproveIC: false,
    canStructureSPV: false,
    canOnboardInvestors: true,
    canViewICSubmissions: false,
    canViewInternalDataRoom: true,
    canViewExternalDealRoom: false,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: true,
    canViewAllPortfolio: false,
    canViewOperations: true,
  },
  [ROLES.PLATFORM_ADMIN]: {
    canSourceDeals: true,
    canSubmitIC: true,
    canApproveIC: true,
    canStructureSPV: true,
    canOnboardInvestors: true,
    canViewICSubmissions: true,
    canViewInternalDataRoom: true,
    canViewExternalDealRoom: true,
    canTokenise: true,
    canAdminTokens: true,
    canManageTeam: true,
    canManagePermissions: true,
    canViewAllPortfolio: true,
    canViewOperations: true,
  },
  [ROLES.EXTERNAL_INVESTOR]: {
    canSourceDeals: false,
    canSubmitIC: false,
    canApproveIC: false,
    canStructureSPV: false,
    canOnboardInvestors: false,
    canViewICSubmissions: false,
    canViewInternalDataRoom: false,
    canViewExternalDealRoom: true,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: false,
    canViewOperations: false,
  },
  [ROLES.DEAL_ORIGINATOR]: {
    canSourceDeals: true,
    canSubmitIC: false,
    canApproveIC: false,
    canStructureSPV: false,
    canOnboardInvestors: false,
    canViewICSubmissions: false,
    canViewInternalDataRoom: false,
    canViewExternalDealRoom: true,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: false,
    canViewOperations: false,
  },
  [ROLES.CO_INVESTOR]: {
    canSourceDeals: false,
    canSubmitIC: false,
    canApproveIC: false,
    canStructureSPV: false,
    canOnboardInvestors: false,
    canViewICSubmissions: false,
    canViewInternalDataRoom: false,
    canViewExternalDealRoom: true,
    canTokenise: false,
    canAdminTokens: false,
    canManageTeam: false,
    canManagePermissions: false,
    canViewAllPortfolio: false,
    canViewOperations: false,
  },
};

// ─── Helper Utilities ─────────────────────────────────────────────────────────

/** Returns true if the role is an internal platform role */
export function isInternalRole(role) {
  return ROLE_PROFILES[role]?.category === 'internal';
}

/** Returns true if the role is an external deal-scoped role */
export function isExternalRole(role) {
  return ROLE_PROFILES[role]?.category === 'external';
}

/** Returns true if the role has a specific nav section permission */
export function hasNavPermission(role, sectionId) {
  return ROLE_NAV_PERMISSIONS[role]?.includes(sectionId) ?? false;
}

/** Returns the module permission map for a role */
export function getModulePermissions(role) {
  return ROLE_MODULE_PERMISSIONS[role] ?? {};
}

/** Returns the display profile for a role */
export function getRoleProfile(role) {
  return ROLE_PROFILES[role] ?? ROLE_PROFILES[ROLES.INVESTMENT_TEAM];
}

/** Default role for first load */
export const DEFAULT_ROLE = ROLES.INVESTMENT_TEAM;

// ─── Demo Identity ────────────────────────────────────────────────────────────
// Single signed-in identity for the prototype. Does not change on context switch.

export const DEMO_USER = {
  name:     'James Donovan',
  email:    'james.donovan@seeddrive.com',
  initials: 'JD',
  title:    'Managing Director',
};

// ─── Demo Contexts ────────────────────────────────────────────────────────────
// Ordered list of acting contexts available to the demo user.
// Each context resolves to a role — the role drives all nav/permission logic.
//
// entityType: 'fund' | 'spv' | 'deal'
// group:      'internal' | 'external'

export const DEFAULT_CONTEXT_ID = 'ctx-investment-team';

export const DEMO_CONTEXTS = [
  // Internal — fund level
  {
    id:         'ctx-investment-team',
    entity:     'Greenfield Renewables Fund I',
    entityType: 'fund',
    role:       ROLES.INVESTMENT_TEAM,
    group:      'internal',
  },
  {
    id:         'ctx-ic-member',
    entity:     'Greenfield Renewables Fund I',
    entityType: 'fund',
    role:       ROLES.IC_MEMBER,
    group:      'internal',
  },
  {
    id:         'ctx-finance-team',
    entity:     'Greenfield Renewables Fund I',
    entityType: 'fund',
    role:       ROLES.FINANCE_TEAM,
    group:      'internal',
  },
  // Internal — fund level (continued)
  {
    id:         'ctx-asset-mgmt-team',
    entity:     'Greenfield Renewables Fund I',
    entityType: 'fund',
    role:       ROLES.ASSET_MGMT_TEAM,
    group:      'internal',
  },
  {
    id:         'ctx-legal-comp-team',
    entity:     'Greenfield Renewables Fund I',
    entityType: 'fund',
    role:       ROLES.LEGAL_COMP_TEAM,
    group:      'internal',
  },
  {
    id:         'ctx-platform-admin',
    entity:     'Greenfield Renewables Fund I',
    entityType: 'fund',
    role:       ROLES.PLATFORM_ADMIN,
    group:      'internal',
  },
  // External — deal scoped
  {
    id:         'ctx-external-investor',
    entity:     'GlobalInfra Partners',
    entityType: 'investor',
    role:       ROLES.EXTERNAL_INVESTOR,
    group:      'external',
  },
  {
    id:         'ctx-co-investor',
    entity:     'Apex Infrastructure Partners',
    entityType: 'investor',
    role:       ROLES.CO_INVESTOR,
    group:      'external',
  },
  {
    id:         'ctx-deal-originator',
    entity:     'Lagos Solar Platform',
    entityType: 'deal',
    role:       ROLES.DEAL_ORIGINATOR,
    group:      'external',
  },
];
